import type { BookData } from '../interfaces/book-data.interface';
import type { SpreadData } from '../interfaces/spread-data.interface';
import { isDrawableImage, type DrawableData } from '../interfaces/drawable-data.interface';
import { findDrawableDeep } from './drawable-tree';

export type PrintAssetFailureReason = 'unavailable' | 'undecodable' | 'not_ready';

/** One photo the print renderer refused, as its `error` callback names it. */
export interface PrintAssetFailure {
  readonly placement: 'spread' | 'cover';
  /** 0-based spread index; absent for cover. */
  readonly spread?: number;
  /** Box / drawable id. */
  readonly key: string;
  readonly reason: PrintAssetFailureReason;
}

/** The same failure in the book's words; `fileId`/`filename` null when the key no longer resolves. */
export interface PrintPhotoFailure {
  readonly fileId: string | null;
  readonly filename: string | null;
  readonly placement: 'spread' | 'cover';
  /** 1-based, as the editor numbers spreads; null for cover. */
  readonly spreadNumber: number | null;
  readonly reason: PrintAssetFailureReason;
}

export interface BookPrintFailureDetails {
  readonly photos: readonly PrintPhotoFailure[];
}

/** Resolves each renderer key on its own surface, so a box id repeated on another spread never answers. */
export function resolvePrintAssets(book: BookData | null | undefined, assets: readonly PrintAssetFailure[]): PrintPhotoFailure[] {
  return assets.map(asset => {
    const cover = asset.placement === 'cover';
    const surface = cover ? book?.cover : book?.spreads?.[asset.spread ?? -1];
    let drawable: DrawableData | undefined;
    for (const collage of surface?.collages ?? []) {
      drawable = findDrawableDeep(collage.drawableObjects ?? [], d => d.id === asset.key);
      if (drawable) break;
    }

    const image = drawable && isDrawableImage(drawable) ? drawable.image : null;

    return {
      fileId: image?.id || null,
      filename: image?.filename || null,
      placement: asset.placement,
      spreadNumber: cover || asset.spread === undefined ? null : asset.spread + 1,
      reason: asset.reason,
    };
  });
}

/** A refusal waiting cannot clear: the file is gone or unreadable, not a photo still processing. */
export function isPermanentPrintFailure(asset: { reason: PrintAssetFailureReason }): boolean {
  return asset.reason !== 'not_ready';
}

/** A refused photo the photographer clears by replacing it: a known file, not one still processing. */
export function isReplaceablePrintFailure(photo: Pick<PrintPhotoFailure, 'fileId' | 'reason'>): boolean {
  return !!photo.fileId && isPermanentPrintFailure(photo);
}

/** A refused photo where it sits in the book NOW; `spreadId` = the strip's jump target (`'cover'` or a spread uuid). */
export interface PlacedPrintFailure extends PrintPhotoFailure {
  readonly spreadId: string | null;
}

/**
 * Re-anchors a failure row to the current model: gone when its photo is no longer placed (replaced or
 * removed), else moved to the spread that holds it now — the reported index goes stale after a spread
 * delete. A row whose key never resolved (`fileId` null) cannot be traced and stays as reported.
 */
export function placePrintFailure(model: BookData | null, photo: PrintPhotoFailure): PlacedPrintFailure | null {
  const spreads = model?.spreads ?? [];
  if (!model || !photo.fileId) {
    const spreadId = photo.placement === 'cover' ? 'cover' : (spreads[(photo.spreadNumber ?? 0) - 1]?.uuid ?? null);
    return { ...photo, spreadId };
  }

  const holds = (surface: Pick<SpreadData, 'collages'> | null | undefined): boolean =>
    (surface?.collages ?? []).some(
      collage => !!findDrawableDeep(collage.drawableObjects ?? [], d => isDrawableImage(d) && d.image?.id === photo.fileId),
    );
  const reported = (photo.spreadNumber ?? 0) - 1;
  const index = holds(spreads[reported]) ? reported : spreads.findIndex(holds);
  if (holds(model.cover) && (photo.placement === 'cover' || index < 0)) {
    return { ...photo, placement: 'cover', spreadNumber: null, spreadId: 'cover' };
  }

  return index < 0 ? null : { ...photo, placement: 'spread', spreadNumber: index + 1, spreadId: spreads[index].uuid };
}
